import { useCallback, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Divider,
    Stack,
    Typography,
    Alert,
    AlertTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../app/hooks';
import { UrsConsistencyValidationResult, validateUrsConsistency } from './ursConsistencyValidatorApi';
import EditIcon from '@mui/icons-material/Edit';
import { UrsGroup, URSRequirement, UrsRequirementType } from '../UrsSlice';
import { SimpleEditUrsRequirementDialog } from './SimpleEditUrsRequirementDialog';
import CloseIcon from '@mui/icons-material/Close';
import { DeleteGroupDialog } from '../dialogs/DeleteGroupDialog';
import { Localized } from '@fluent/react';
import DeleteIcon from '@mui/icons-material/Delete';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

export const UrsConsistencyValidator = () => {
    const { id: paramId } = useParams();
    const documentId = paramId ?? '';
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<UrsConsistencyValidationResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedRequirement, setSelectedRequirement] = useState<URSRequirement | null>(null);
    const [editedRequirementIds, setEditedRequirementIds] = useState<string[]>([]);
    const [deletedGroupIds, setDeletedGroupIds] = useState<string[]>([]);
    const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
    const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);


    const handleGenerateClick = async () => {
        try {
            setLoading(true);
            setError(null);
            setEditedRequirementIds([]);
            setDeletedGroupIds([])
            const actionResult = await dispatch(validateUrsConsistency({ documentId }));

            if (validateUrsConsistency.fulfilled.match(actionResult)) {
                const data = actionResult.payload as UrsConsistencyValidationResult;
                setResult(data);
            } else {
                let errorMsg = 'error';
                if (actionResult.payload && (actionResult.payload as any).kind === 'http') {
                    const problem = (actionResult.payload as any).problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setError(errorMsg);
            }
        } catch (e: any) {
            setError(e.message ?? 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditRequirement = useCallback(
        (requirement: URSRequirement) => {
            setSelectedRequirement(requirement);
            setEditDialogOpen(true);
        },
        [],
    );

    const handleRequirementUpdated = useCallback(
        (updated: URSRequirement) => {
            setResult(prev => {
                if (!prev) return prev;

                return {
                    ...prev,
                    requirementsWithPlaceholders: prev.requirementsWithPlaceholders.map(r =>
                        r.id === updated.id
                            ? {
                                ...r,
                                name: updated.name ?? r.name,
                            }
                            : r
                    ),
                };
            });
            setEditedRequirementIds(prev =>
                prev.includes(updated.id) ? prev : [...prev, updated.id]
            );
        },
        [],
    );

    const handleGroupDeleted = useCallback(
        (selectedGroupId: string) => {
            setDeletedGroupIds(prev =>
                prev.includes(selectedGroupId) ? prev : [...prev, selectedGroupId]
            );
        },
        [],
    );
    const handleDeleteGroupClick = (group: UrsGroup) => {
        if (!group)
            return;

        setDeleteGroupDialogOpen(true);
        setSelectedGroupId(group.id);
    };

    const handleDeleteTypeClick = (type: UrsRequirementType) => {
        if (!type)
            return;

        setSelectedGroupId(type.id);
        setDeleteGroupDialogOpen(true);

    };

    const handleBackButtonClick = useCallback(() => {
        navigate("/urs/edit/" + documentId);
    }, [navigate, documentId]);

    const hasAnyIssues =
        !!(result && (result.hasPlaceholders || result.hasEmptyGroups || result.hasEmptyTypes));

    return (
        <Container maxWidth="lg">
            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={3}>
                        {result && (
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: 'background.default',
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <FactCheckIcon />
                                    <Typography variant="subtitle1">
                                        <Localized id="urs-consistency-check-report-title">
                                            <span>Raport spójności dokumentu URS</span>
                                        </Localized>
                                    </Typography>
                                </Stack>

                                <Stack direction="row" spacing={1}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleGenerateClick}
                                        disabled={loading}
                                        startIcon={!loading ? <FactCheckIcon /> : undefined}
                                    >
                                        {loading ? (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CircularProgress size={18} />
                                                <Localized id="urs-consistency-check-loading">
                                                    <span>_Generowanie...</span>
                                                </Localized>
                                            </Stack>
                                        ) : (
                                            <Localized id="urs-consistency-check-regenerate-button">
                                                <span>_Generuj ponownie</span>
                                            </Localized>
                                        )}
                                    </Button>

                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleBackButtonClick}
                                        startIcon={<CloseIcon />}
                                    >
                                        <Localized id="close">
                                            <span>_Zamknij</span>
                                        </Localized>
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {!result && (
                            <Box
                                sx={{
                                    borderRadius: 2,
                                    borderStyle: 'dashed',
                                    borderWidth: 1,
                                    borderColor: 'divider',
                                    p: 3,
                                    textAlign: 'center',
                                }}
                            >
                                <Stack spacing={2} alignItems="center">
                                    <FactCheckIcon sx={{ fontSize: 48 }} />
                                    <Typography variant="h6">
                                        <Localized id="urs-consistency-check-empty-state-title">
                                            <span>_Generowanie raportu spójności</span>
                                        </Localized>
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        <Localized id="urs-consistency-check-empty-state-description">
                                            <span>
                                                _click to generate
                                            </span>
                                        </Localized>
                                    </Typography>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={handleGenerateClick}
                                        disabled={loading}
                                        startIcon={!loading ? <FactCheckIcon /> : undefined}
                                    >
                                        {loading ? (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CircularProgress size={20} />
                                                <Localized id="urs-consistency-check-loading">
                                                    <span>_Generowanie...</span>
                                                </Localized>
                                            </Stack>
                                        ) : (
                                            <Localized id="urs-consistency-check-generate-button">
                                                <span>_Generuj raport spójności</span>
                                            </Localized>
                                        )}
                                    </Button>
                                    <Button
                                        variant="text"
                                        size="large"
                                        onClick={() => navigate(-1)}
                                    >
                                        <Localized id="close">
                                            <span>_Zamknij</span>
                                        </Localized>
                                    </Button>
                                </Stack>
                            </Box>
                        )}

                        {error && (
                            <Alert severity="error">
                                <AlertTitle>Błąd</AlertTitle>
                                <Localized id={error}>
                                    <span>_Error</span>
                                </Localized>
                            </Alert>
                        )}

                        {result && (
                            <Box>
                                <Divider sx={{ mb: 2 }} />
                                {hasAnyIssues ? (
                                    <Alert severity="warning" icon={<WarningAmberIcon />}>
                                        <AlertTitle>
                                            <Localized id="urs-consistency-check-warning-title">
                                                <span>_Wykryto potencjalne problemy</span>
                                            </Localized>
                                        </AlertTitle>
                                        <Typography variant="body2">
                                            <Localized id="urs-consistency-check-warning-description">
                                                <span>_W dokumencie znaleziono:</span>
                                            </Localized>
                                        </Typography>
                                        <Box mt={1}>
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                <Chip
                                                    size="small"
                                                    label={
                                                        <Localized
                                                            id="urs-consistency-check-chip-requirements-with-issues"
                                                            vars={{ count: result.requirementsWithPlaceholders.length }}
                                                        >
                                                            <span>
                                                                _Wymagania z brakami
                                                            </span>
                                                        </Localized>
                                                    }
                                                />
                                                <Chip
                                                    size="small"
                                                    label={
                                                        <Localized
                                                            id="urs-consistency-check-chip-empty-groups"
                                                            vars={{ count: result.emptyGroups.length }}
                                                        >
                                                            <span>
                                                                _Puste grupy
                                                            </span>
                                                        </Localized>
                                                    }
                                                />
                                                <Chip
                                                    size="small"
                                                    label={
                                                        <Localized
                                                            id="urs-consistency-check-chip-empty-types"
                                                            vars={{ count: result.emptyTypes.length }}
                                                        >
                                                            <span>
                                                                _Typy bez wymagań
                                                            </span>
                                                        </Localized>
                                                    }
                                                />
                                            </Stack>
                                        </Box>
                                    </Alert>
                                ) : (
                                    <Alert severity="success" icon={<CheckCircleIcon />}>
                                        <AlertTitle>
                                            <Localized id="urs-consistency-check-success-title">
                                                <span>Brak problemów</span>
                                            </Localized>
                                        </AlertTitle>

                                        <Typography variant="body2">
                                            <Localized id="urs-consistency-check-success-description">
                                                <span>
                                                    Nie wykryto placeholderów, pustych grup ani typów bez wymagań.
                                                </span>
                                            </Localized>
                                        </Typography>
                                    </Alert>
                                )}
                            </Box>
                        )}

                        {result && hasAnyIssues && (
                            <Stack spacing={4}>
                                {result.requirementsWithPlaceholders.length > 0 && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Localized id="urs-consistency-check-section-requirements-title">
                                                <span>_Wymagania z brakującymi wartościami / placeholderami</span>
                                            </Localized>
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            <Localized id="urs-consistency-check-section-requirements-description">
                                                <span>
                                                    _list
                                                </span>
                                            </Localized>
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{
                                                        maxWidth: 120,
                                                        width: 120,
                                                        minWidth: 120,
                                                    }}>
                                                        <Localized id="number">
                                                            <span>_Numer</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Localized id="title">
                                                            <span>_Tytuł</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell align="right">
                                                        <Localized id="actions">
                                                            <span>_Akcje</span>
                                                        </Localized>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.requirementsWithPlaceholders.map((r) => (
                                                    <TableRow key={r.requirementId}>
                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>{r.numeration}</TableCell>
                                                        <TableCell><div dangerouslySetInnerHTML={{ __html: r.name }}></div></TableCell>
                                                        <TableCell>
                                                            {editedRequirementIds.includes(r.id) && (
                                                                <Tooltip title={
                                                                    <Localized id="urs-consistency-check-tooltip-edited-in-session">
                                                                        <span>Zmienione w tej sesji</span>
                                                                    </Localized>
                                                                }>
                                                                    <CheckCircleIcon fontSize="small" color="success" />
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title={
                                                                <Localized id="edit">
                                                                    <span>_edit</span>
                                                                </Localized>
                                                            }>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        handleEditRequirement(r)
                                                                    }
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}

                                {result.emptyGroups.length > 0 && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Localized id="urs-consistency-check-section-empty-groups-title">
                                                <span>_Grupy bez wymagań</span>
                                            </Localized>
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            <Localized id="urs-consistency-check-section-empty-groups-description">
                                                <span>
                                                    _group list
                                                </span>
                                            </Localized>
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            maxWidth: 120,
                                                            width: 120,
                                                            minWidth: 120,
                                                        }}
                                                    >
                                                        <Localized id="number">
                                                            <span>_Numer</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Localized id="group">
                                                            <span>_Grupa</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Localized id="actions">
                                                            <span>_Akcje</span>
                                                        </Localized>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.emptyGroups.map((g) => (
                                                    <TableRow key={g.id}>
                                                        <TableCell>
                                                            {g.numeration ?? '-'}
                                                        </TableCell>

                                                        <TableCell
                                                            sx={{
                                                                width: '100%',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: 1,
                                                                }}
                                                            >
                                                                <span>{g.name}</span>

                                                                {deletedGroupIds.includes(g.id) && (
                                                                    <Tooltip title={
                                                                        <Localized id="urs-consistency-check-tooltip-deleted-in-session">
                                                                            <span>_Usunięte w tej sesji</span>
                                                                        </Localized>
                                                                    }>
                                                                        <HighlightOffIcon fontSize="small" color="error" />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            <Tooltip title={<Localized id="delete"><span>_delete</span></Localized>}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteGroupClick(g)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>

                                    </Box>
                                )}

                                {result.emptyTypes.length > 0 && (
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            <Localized id="urs-consistency-check-section-empty-types-title">
                                                <span>_Typy bez wymagań</span>
                                            </Localized>
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            gutterBottom
                                        >
                                            <Localized id="urs-consistency-check-section-empty-types-description">
                                                <span>
                                                    _types
                                                </span>
                                            </Localized>
                                        </Typography>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell
                                                        sx={{
                                                            maxWidth: 120,
                                                            width: 120,
                                                            minWidth: 120,
                                                        }}>
                                                        <Localized id="type">
                                                            <span>Typ</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Localized id="name">
                                                            <span>_Nazwa</span>
                                                        </Localized>
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell align="right">
                                                        <Localized id="actions">
                                                            <span>_Akcje</span>
                                                        </Localized>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {result.emptyTypes.map((t) => (
                                                    <TableRow key={t.id}>
                                                        <TableCell>{t.code}</TableCell>
                                                        <TableCell>{t.name}</TableCell>
                                                        <TableCell>
                                                            {deletedGroupIds.includes(t.id) && (
                                                                <Tooltip title="Usunięte w tej sesji">
                                                                    <HighlightOffIcon fontSize="small" color="error" />
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title={<Localized id="delete">_delete</Localized>}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteTypeClick(t)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>
            {
                editDialogOpen && selectedRequirement && (
                    <SimpleEditUrsRequirementDialog
                        requirement={selectedRequirement}
                        onSuccess={updatedRow => {
                            setEditDialogOpen(false);
                            setSelectedRequirement(null);
                            handleRequirementUpdated(updatedRow);
                        }}
                        onCancel={() => {
                            setEditDialogOpen(false);
                            setSelectedRequirement(null);
                        }}
                    />
                )
            }
            {
                deleteGroupDialogOpen && selectedGroupId && <DeleteGroupDialog
                    id={selectedGroupId}
                    onSuccess={() => {
                        handleGroupDeleted(selectedGroupId)
                        setDeleteGroupDialogOpen(false);
                        setSelectedGroupId(null);
                    }}
                    onCancel={() => {
                        setDeleteGroupDialogOpen(false);
                        setSelectedGroupId(null);
                    }}
                />
            }
        </Container >

    );
};
